/**
 * Bottom Navigation Component
 * Fixed bottom navigation bar for mobile
 */

import { Link, useLocation, useRouter } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { m } from 'framer-motion';
import { useState, useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import styles from './BottomNavigation.module.less';
import { Home, Search, Store, User, Users } from '@/components/ui/icons';
import { useNetworkAware } from '@/hooks/useNetworkAware';
import { useHapticTarget } from '@/contexts/HapticsContext';
import { triggerHapticFeedback } from '@/lib/haptics';
import { prefetchDetailData, type DetailQuery } from '@/lib/prefetch/detail-data';
import {
  communityPostsRootQuery,
  discoveryClassesRootQuery,
  homeFeatureRequestsQuery,
  homeUpcomingClassesQuery,
  marketProductsRootQuery,
  profileRootQuery,
} from '@/lib/queries/detail';

const navItems = [
  {
    id: 'home',
    label: 'Home',
    icon: Home,
    path: '/home',
  },
  {
    id: 'discovery',
    label: 'Discover',
    icon: Search,
    path: '/discovery',
  },
  {
    id: 'market',
    label: 'Market',
    icon: Store,
    path: '/market',
  },
  {
    id: 'community',
    label: 'Community',
    icon: Users,
    path: '/community',
  },
  {
    id: 'profile',
    label: 'Profile',
    icon: User,
    path: '/profile',
  },
];

// Per-tab destination root queries, evaluated at PREFETCH time (thunk, not a
// module-level object) because discoveryClassesRootQuery embeds the SGT "today"
// key, which must be fresh when the finger lands rather than when this module
// loads. Each factory is byte-identical to what the destination's route loader
// seeds, and every loader is cache-first gated on that exact key — so an entry
// primed here resolves the committed navigation instantly instead of paying a
// full API round trip after the first tap of a session. /feature-requests has
// no bottom-nav item; its root feed is already warmed by the alpha-card
// viewport prefetch when home renders.
const TAB_ROOT_QUERIES: Record<string, () => DetailQuery[]> = {
  '/home': () => [homeFeatureRequestsQuery(), homeUpcomingClassesQuery()],
  '/discovery': () => [discoveryClassesRootQuery()],
  '/market': () => [marketProductsRootQuery()],
  '/community': () => [communityPostsRootQuery()],
  '/profile': () => [profileRootQuery()],
};

// Static device capability: does this device have a real pointer (mouse/trackpad)?
// Computed once at module load — it cannot change during a session. Touch devices
// don't get the framer-motion hover variant, which otherwise contributes to the
// iOS "tap twice to activate" double-tap behavior.
const CAN_HOVER =
  typeof window !== 'undefined' && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

// Reactive reduced-motion preference (user can toggle it mid-session, so we
// subscribe) — avoids re-running matchMedia on every render.
const subscribePrefersReducedMotion = (callback: () => void) => {
  if (typeof window === 'undefined') return () => {};
  const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
  mql.addEventListener('change', callback);
  return () => mql.removeEventListener('change', callback);
};
const getPrefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const getServerSnapshot = () => false;

// Individual nav item component with iOS-style long press effect
function NavItem({
  item,
  isActive,
  onPressStart,
  onPressEnd,
  isPressedGlobally,
  onPrefetch,
  wasActive,
}: {
  item: (typeof navItems)[0];
  isActive: boolean;
  onPressStart: (itemId: string) => void;
  onPressEnd: () => void;
  isPressedGlobally: string | null;
  onPrefetch: (path: string) => void;
  wasActive: boolean;
}) {
  const isPressed = isPressedGlobally === item.id;
  const Icon = item.icon;
  const hapticRef = useHapticTarget<HTMLElement>();

  // Reactive OS reduced-motion setting (subscribed, not recomputed per render)
  const prefersReducedMotion = useSyncExternalStore(
    subscribePrefersReducedMotion,
    getPrefersReducedMotion,
    getServerSnapshot
  );

  // Icon animation variants - transition only, no end-state change
  const iconVariants = {
    inactive: {
      scale: 1,
      rotate: 0,
    },
    active: prefersReducedMotion
      ? {
          scale: 1,
          rotate: 0,
        }
      : {
          scale: [1, 1.15, 1], // More pronounced bounce on activation
          rotate: [0, -5, 5, -5, 0], // Subtle wiggle
          transition: {
            duration: 0.4,
            ease: [0.25, 0.1, 0.25, 1.0] as const,
          },
        },
    hover: prefersReducedMotion
      ? {
          scale: 1,
        }
      : {
          scale: 1.05,
          transition: { duration: 0.2 },
        },
    pressed: prefersReducedMotion
      ? {
          scale: 1,
        }
      : {
          scale: 0.92,
          transition: { duration: 0.1 },
        },
    deactivate: prefersReducedMotion
      ? {
          scale: 1,
        }
      : {
          scale: [1, 0.95, 1], // Subtle pulse when deactivating
          transition: {
            duration: 0.2,
            ease: 'easeOut' as const,
          },
        },
  };

  return (
    <Link
      to={item.path}
      ref={hapticRef}
      className={styles.bottomNavigationItem}
      aria-label={item.label}
      aria-current={isActive ? 'page' : undefined}
      onKeyDown={(e) => {
        // Keyboard navigation support
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          triggerHapticFeedback();
          onPressStart(item.id);
        }
      }}
      onKeyUp={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onPressEnd();
        }
      }}
      onPointerDown={() => {
        onPressStart(item.id);
      }}
      onPointerEnter={(e) => {
        // Prefetch route on hover (desktop) - instant navigation feel
        onPrefetch(item.path);

        // If pointer is down (button pressed) and enters this item
        if (e.buttons === 1) {
          onPressStart(item.id);
        }
      }}
      onTouchStart={() => {
        // Prefetch route on touch start (mobile) - instant navigation feel
        onPrefetch(item.path);
      }}
      onPointerUp={onPressEnd}
      onPointerLeave={(e) => {
        // Only reset if pointer has left and is not pressed
        if (e.buttons !== 1) {
          onPressEnd();
        }
      }}
      onPointerCancel={onPressEnd}
    >
      {/* Pill-shaped background for active state with iOS glass effect and subtle glow */}
      {isActive && (
        <m.div
          className={styles.bottomNavigationItemBackground}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{
            opacity: 1,
            scale: isPressed && !prefersReducedMotion ? 1.05 : 1,
            y: isPressed && !prefersReducedMotion ? -6 : 0,
            boxShadow: isPressed
              ? '0 12px 24px rgba(99, 102, 241, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.5), 0 0 20px rgba(99, 102, 241, 0.15)'
              : '0 2px 8px rgba(99, 102, 241, 0.12), 0 0 0 1px rgba(99, 102, 241, 0.08)',
          }}
          exit={{
            opacity: 0,
            scale: 0.8,
          }}
          transition={
            prefersReducedMotion
              ? { duration: 0 }
              : {
                  type: 'spring',
                  stiffness: 400,
                  damping: 25,
                }
          }
          layoutId="activeTabBackground"
        />
      )}

      {/* Icon */}
      <m.div
        animate={
          isActive ? 'active' : wasActive ? 'deactivate' : isPressed ? 'pressed' : 'inactive'
        }
        variants={iconVariants}
        whileHover={CAN_HOVER ? 'hover' : undefined}
      >
        <Icon
          className={clsx(
            styles.bottomNavigationIcon,
            isActive ? styles.navItemActive : styles.navItemInactive
          )}
        />
      </m.div>

      {/* Label with staggered fade-in effect */}
      <m.span
        className={clsx(
          styles.bottomNavigationLabel,
          isActive ? styles.navItemActive : styles.navItemInactive
        )}
        initial={{ opacity: 0.7, y: 2 }}
        animate={{
          opacity: isActive ? 1 : 0.7,
          y: isActive && !prefersReducedMotion ? 0 : 2,
          scale: isActive && !prefersReducedMotion ? 1.02 : 1,
        }}
        transition={
          prefersReducedMotion
            ? { duration: 0 }
            : {
                duration: 0.2,
                ease: 'easeOut',
              }
        }
      >
        {item.label}
      </m.span>
    </Link>
  );
}

// Hide-on-scroll state machine (pure, unit-tested in BottomNavigation.test.tsx).
// Returns the next `hidden` value for a scroll event. Under reduced motion the
// bar is never hidden by scrolling — and if the user enables it mid-session
// while the bar is hidden, it comes back immediately (issue #872).
export function nextBottomNavHidden(
  currentY: number,
  lastScrollY: number,
  hidden: boolean,
  reducedMotion: boolean
): boolean {
  if (reducedMotion) return false;
  if (currentY <= 0) return false;
  const delta = currentY - lastScrollY;
  if (currentY > 64 && delta > 8) return true;
  if (delta < -8) return false;
  return hidden;
}

export function BottomNavigation() {
  const location = useLocation();
  const router = useRouter();
  const currentPath = location.pathname;
  const [pressedItem, setPressedItem] = useState<string | null>(null);
  // Last active tab, read to decide whether a tab needs the deactivate pulse.
  // Kept in a ref rather than state: the value is derived from the path, and
  // mirroring it through state cost an extra render per tab switch.
  const previousActiveRef = useRef<string | undefined>(undefined);
  const { saveData } = useNetworkAware();
  const queryClient = useQueryClient();

  // Prefetch route data on hover/touch for instant navigation
  const handlePrefetch = useCallback(
    (path: string) => {
      // Honor Data Saver (an explicit user opt-out), but do NOT gate on
      // isSlowConnection: this prefetch fires on an explicit hover/touch and
      // costs one small route chunk, while slow connections — congested 4G is
      // the primary phone audience — are exactly where an instant tab switch
      // matters most. The old isSlowConnection gate silently dropped tab
      // prefetching there entirely.
      if (saveData) return;

      // Skip if already on this route
      if (currentPath === path) return;

      // Use TanStack Router's preload feature
      router.preloadRoute({ to: path }).catch(() => {
        // Silently fail - prefetching is an optimization, not critical
      });

      // Warm the destination's root DATA alongside the chunk — the chunk alone
      // still left the first tab tap paying one API round trip after commit.
      // prefetchDetailData TTL-throttles per query key (60s), so repeated
      // touches and desktop hovers never refire, and errors are swallowed
      // (the real navigation's loader owns retry/error UX).
      const rootQueries = TAB_ROOT_QUERIES[path];
      if (rootQueries) {
        for (const q of rootQueries()) void prefetchDetailData(queryClient, q);
      }
    },
    [router, currentPath, saveData, queryClient]
  );

  // Track active item for deactivation animation
  const activeItem = navItems.find(
    (item) => currentPath === item.path || currentPath.startsWith(item.path + '/')
  );
  const previouslyActive = previousActiveRef.current;
  previousActiveRef.current = activeItem?.id;

  const prefersReducedMotion = useSyncExternalStore(
    subscribePrefersReducedMotion,
    getPrefersReducedMotion,
    getServerSnapshot
  );

  // Hide on scroll-down, reveal on scroll-up. A small delta threshold avoids
  // jitter; the bar stays anchored near the top. Disabled for reduced-motion users.
  const [hidden, setHidden] = useState(false);
  const lastScrollY = useRef(0);

  useEffect(() => {
    if (prefersReducedMotion) {
      // Reduced motion: the bar is never hidden by scrolling, so surface it even
      // if the user toggled the OS preference mid-session while it was hidden
      // (otherwise primary navigation is stranded offscreen and unreachable).
      setHidden(false);
      return;
    }
    // Capture phase: the app scrolls an inner element with overflow-y:auto (not the
    // window), so we catch scroll from any descendant container and read its scrollTop.
    const onScroll = (event: Event) => {
      // If the document itself scrolled, read from scrollingElement (<html>);
      // otherwise the target is the inner overflow container.
      const el = (
        event.target === document ? document.scrollingElement : event.target
      ) as HTMLElement | null;
      const currentY = el?.scrollTop ?? 0;
      setHidden((prev) => nextBottomNavHidden(currentY, lastScrollY.current, prev, false));
      lastScrollY.current = currentY;
    };
    window.addEventListener('scroll', onScroll, { passive: true, capture: true });
    return () => window.removeEventListener('scroll', onScroll, { capture: true as never });
  }, [prefersReducedMotion]);

  // Route-level visibility is controlled by parent route.tsx; this component always
  // renders when called. Scroll-driven visibility is handled above.
  return (
    <m.nav
      className={styles.bottomNavigation}
      role="navigation"
      aria-label="Main navigation"
      style={{ x: '-50%' }}
      animate={{ y: hidden ? '100%' : 0, pointerEvents: hidden ? 'none' : 'auto' }}
      transition={
        prefersReducedMotion ? { duration: 0 } : { type: 'spring', stiffness: 400, damping: 35 }
      }
    >
      <div className={styles.bottomNavigationContainer}>
        {/* a11y: no role="list" — a <nav> of links is the accessible pattern; role=list
              requires listitem children (Lighthouse aria-required-children). */}
        <div className={styles.bottomNavigationItems}>
          {navItems.map((item) => {
            // Check if current path matches or starts with the nav item path (for nested routes)
            const isActive = currentPath === item.path || currentPath.startsWith(item.path + '/');
            const wasActive = previouslyActive === item.id && !isActive;

            return (
              <NavItem
                key={item.id}
                item={item}
                isActive={isActive}
                onPressStart={(itemId) => setPressedItem(itemId)}
                onPressEnd={() => setPressedItem(null)}
                isPressedGlobally={pressedItem}
                onPrefetch={handlePrefetch}
                wasActive={wasActive}
              />
            );
          })}
        </div>
      </div>
    </m.nav>
  );
}
